<script lang="ts" setup>
import { useKeenSlider } from 'keen-slider/vue'
import 'keen-slider/keen-slider.min.css'

const props = defineProps<{
  data: {
    translations: DirectusTranslation<{
      title: string
      section_title: string
    }>
    items: {
      id: number
      block_carousel_image_select_items_id: {
        id: number
        button_image: string
        image: string
        translations: DirectusTranslation<{
          description: string
        }>
      }
    }[]
  }
}>()

const { dt } = useDirectusTranslation()
const { hyphen } = useHyphen()

const items = computed(() => {
  return props.data.items.map((item) => {
    return {
      ...item.block_carousel_image_select_items_id,
      ...dt(item.block_carousel_image_select_items_id.translations),
      translations: undefined,
    }
  })
})

const activeItemSlide = ref(0)

const [container, slider] = useKeenSlider({
  loop: false,
  renderMode: 'performance',
  drag: true,
  slides: {
    perView: 1,
  },
  slideChanged: (s) => {
    activeItemSlide.value = s.track.details.rel
  },
})

function nextSlide() {
  slider?.value?.next()
}
function prevSlide() {
  slider?.value?.prev()
}

slider.value?.moveToIdx(0)

const activeItemIndex = computed({
  get: () => activeItemSlide.value,
  set: (value) => {
    if (value !== activeItemSlide.value)
      slider?.value?.moveToIdx(value)
  },
})

const activeItemIndexString = computed({
  get: () => activeItemIndex.value.toString(),
  set: (value) => {
    activeItemIndex.value = Number.parseInt(value)
  },

})

const activeItem = computed(() => {
  return items.value[activeItemIndex.value]
})
</script>

<template>
  <BlockBase is-light class="bg-neutral-white py-4 text-neutral-black lg:py-15rem">
    <Container class="grid grid-cols-1 w-full gap-8 lg:grid-cols-2 lg:gap-10rem">
      <!-- SLIDER -->
      <div
        ref="container" class="keen-slider order-last my-auto h-36rem w-full md:order-none lg:h-65rem"
      >
        <div v-for="item, index in items" :key="index" class="keen-slider__slide size-full">
          <NuxtImg
            loading="lazy"
            provider="directus"
            :src="item.image"
            alt=""
            height="650"
            width="650"
            class="h-full w-full object-cover"
          />
        </div>

        <div v-if="items.length > 1" class="absolute bottom-0 right-0 flex items-center justify-center bg-starline-secondary lg:h-10rem lg:w-15rem block-light:bg-neutral-white">
          <button class="h-8rem w-6rem flex items-center justify-center transition lg:h-10rem lg:w-7.5rem hover:bg-neutral-black/10 block-light:hover:bg-starline-secondary/10" @click="prevSlide">
            <IconSmallArrow class="h-2rem w-2rem" />
          </button>
          <button class="h-8rem w-6rem flex items-center justify-center transition lg:h-10rem lg:w-7.5rem hover:bg-starline-secondary/10" @click="nextSlide">
            <IconSmallArrow class="h-2rem w-2rem rotate-180" />
          </button>
        </div>
      </div>

      <!-- Content -->
      <div
        class="my-auto w-full"
      >
        <h1 class="text-balance textH1Black lg:mb-4rem">
          {{ hyphen(dt(props.data.translations)?.title) }}
        </h1>
        <TextWithAfterLine v-if="dt(props.data.translations)?.section_title" class="mb-2rem" :text="dt(props.data.translations)?.section_title ?? ''" />
        <div class="mt-2 lg:mt-8rem">
          <RadixRadioGroupRoot
            v-model="activeItemIndexString"
          >
            <div class="w-full flex flex-wrap gap-2rem">
              <RadixRadioGroupItem
                v-for="item, index in items" :key="index" :value="index.toString()"
                class="inline-block cursor-pointer select-none shadow-none ring-0 ring-neutral-darkGray ring-offset-neutral-black transition duration-300ms data-[active=true]:shadow-starline data-[active=true]:ring-3 data-[active=true]:ring-offset-2 data-[active=true]:ring-starline-primary hover:ring-starline-primary"
              >
                <NuxtImg provider="directus" height="85" width="85" :src="item.button_image" alt="" />
              </RadixRadioGroupItem>
            </div>
          </RadixRadioGroupRoot>
          <div>
            <p class="mt-2rem whitespace-nowrap text-1.1rem text-neutral-gray">
              {{ activeItem?.description ?? '&nbsp;' }}
            </p>
          </div>
        </div>
      </div>
    </Container>
  </BlockBase>
</template>

<style>
</style>
